<template>
  <div class="audit-trail-modern">
    <!-- Header Section -->
    <div class="audit-header">
      <div class="audit-header-left">
        <div class="audit-title">
          <i class="pi pi-history"></i>
          <span>{{ $t('projects.audit.title') }}</span>
        </div>
        <div class="audit-stats" v-if="totalElements > 0">
          <span class="stat-badge">
            <i class="pi pi-chart-line"></i>
            {{ totalElements }} {{ $t('projects.audit.activities') }}
          </span>
          <span v-if="filters.action || filters.entityType" class="stat-badge filtered">
            <i class="pi pi-filter"></i>
            Filtered
          </span>
        </div>
      </div>
      
      <div class="audit-header-right">
        <button 
          @click="toggleFilters" 
          class="filter-toggle-btn"
          :class="{ active: showFiltersPanel }"
        >
          <i class="pi pi-filter"></i>
          <span>Filters</span>
          <i class="pi pi-chevron-down" :class="{ rotated: showFiltersPanel }"></i>
        </button>
        <button @click="loadAuditLogs()" class="refresh-btn">
          <i class="pi pi-refresh" :class="{ spinning: loading }"></i>
        </button>
      </div>
    </div>

    <!-- Collapsible Filters Section -->
    <transition name="slide-down">
      <div v-if="showFiltersPanel" class="audit-filters">
        <div class="filters-grid">
          <div class="filter-item">
            <label>
              <i class="pi pi-bolt"></i>
              {{ $t('projects.audit.filterByAction') }}
            </label>
            <select v-model="filters.action" @change="onFilterChange" class="modern-select">
              <option value="">{{ $t('common.all') }}</option>
              <option value="CREATED">{{ $t('projects.audit.actions.created') }}</option>
              <option value="UPDATED">{{ $t('projects.audit.actions.updated') }}</option>
              <option value="DELETED">{{ $t('projects.audit.actions.deleted') }}</option>
              <option value="CLOSED">{{ $t('projects.audit.actions.closed') }}</option>
              <option value="REOPENED">{{ $t('projects.audit.actions.reopened') }}</option>
              <option value="UPLOADED">{{ $t('projects.audit.actions.documentUploaded') }}</option>
              <option value="UPLOADED">{{ $t('projects.audit.actions.photoUploaded') }}</option>
            </select>
          </div>

          <div class="filter-item">
            <label>
              <i class="pi pi-box"></i>
              {{ $t('projects.audit.filterByEntity') }}
            </label>
            <select v-model="filters.entityType" @change="onFilterChange" class="modern-select">
              <option value="">{{ $t('common.all') }}</option>
              <option value="PROJECT">{{ $t('projects.audit.entities.project') }}</option>
              <option value="PROJECT_PHASE">{{ $t('projects.audit.entities.phase') }}</option>
              <option value="PROJECT_TASK">{{ $t('projects.audit.entities.task') }}</option>
              <option value="PROJECT_ISSUE">{{ $t('projects.audit.entities.issue') }}</option>
              <option value="PROJECT_DOCUMENT">{{ $t('projects.audit.entities.document') }}</option>
              <option value="PROJECT_PHOTO">{{ $t('projects.audit.entities.photo') }}</option>
              <option value="PROJECT_DESIGN">{{ $t('projects.audit.entities.design') }}</option>
              <option value="PROJECT_ACCESS">{{ $t('projects.audit.entities.access') }}</option>
              <option value="PROJECT_INTERVENANT">{{ $t('projects.audit.entities.intervenant') }}</option>
              <option value="PROJECT_NOTE">{{ $t('projects.audit.entities.note') }}</option>
              <option value="PROJECT_FINANCE">{{ $t('projects.audit.entities.finance') }}</option>
              <option value="PROJECT_TRANSACTION">{{ $t('projects.audit.entities.transaction') }}</option>
              <option value="PROJECT_NOTIFICATION">{{ $t('projects.audit.entities.notification') }}</option>
              <option value="PROJECT_DOCUMENT_ANNOTATION">{{ $t('projects.audit.entities.documentAnnotation') }}</option>
              <option value="PROJECT_ISSUE_PHOTO">{{ $t('projects.audit.entities.issuePhoto') }}</option>
              <option value="PROVISIONING_REQUEST">{{ $t('projects.audit.entities.provisioningRequest') }}</option>
            </select>
          </div>

          <div class="filter-item filter-actions">
            <button @click="clearFilters" class="clear-filters-btn">
              <i class="pi pi-times"></i>
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Loading State -->
    <div v-if="loading" class="state-container">
      <div class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t('projects.audit.loading') }}</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="state-container">
      <div class="error-state">
        <i class="pi pi-exclamation-circle"></i>
        <h3>Error Loading Audit Trail</h3>
        <p>{{ error }}</p>
        <button @click="loadAuditLogs" class="retry-btn">
          <i class="pi pi-refresh"></i>
          {{ $t('common.retry') }}
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="auditLogs.length === 0" class="state-container">
      <div class="empty-state">
        <i class="pi pi-inbox"></i>
        <h3>{{ $t('projects.audit.noActivities') }}</h3>
        <p>{{ $t('projects.audit.noActivitiesDescription') }}</p>
      </div>
    </div>

    <!-- Audit Table -->
    <div v-else class="audit-table-container">
      <div class="audit-table-wrapper">
        <table class="audit-table">
          <thead>
            <tr>
              <th class="col-timestamp">
                <i class="pi pi-clock"></i>
                Time
              </th>
              <th class="col-action">
                <i class="pi pi-bolt"></i>
                Action
              </th>
              <th class="col-entity">
                <i class="pi pi-box"></i>
                Entity
              </th>
              <th class="col-user">
                <i class="pi pi-user"></i>
                User
              </th>
              <th class="col-description">
                <i class="pi pi-info-circle"></i>
                Description
              </th>
              <th class="col-changes">
                <i class="pi pi-file-edit"></i>
                Changes
              </th>
              <th class="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="log in auditLogs" :key="log.id">
              <tr 
                class="audit-row"
                :class="{ 
                  'row-expanded': expandedDetails.has(log.id),
                  [`row-${getActionClass(log.action)}`]: true 
                }"
                @click="hasDetails(log) ? toggleDetails(log.id) : null"
              >
                <td class="col-timestamp">
                  <div class="timestamp-cell">
                    <span class="time-relative" :title="formatFullTimestamp(log.timestamp)">
                      {{ log.formattedTimestamp }}
                    </span>
                    <span class="time-exact">
                      {{ formatTime(log.timestamp) }}
                    </span>
                  </div>
                </td>

                <td class="col-action">
                  <div class="action-cell">
                    <span class="action-icon" :style="{ backgroundColor: log.actionColor }">
                      {{ log.actionIcon }}
                    </span>
                    <span class="action-label">{{ formatActionLabel(log.action) }}</span>
                  </div>
                </td>

                <td class="col-entity">
                  <div class="entity-cell">
                    <span class="entity-type">{{ getEntityLabel(log.entityType) }}</span>
                    <span class="entity-id">#{{ log.entityId }}</span>
                  </div>
                </td>

                <td class="col-user">
                  <div class="user-cell">
                    <div class="user-avatar">
                      {{ getUserInitials(log.userName || 'User ' + log.userId) }}
                    </div>
                    <span class="user-name">{{ log.userName || 'User ' + log.userId }}</span>
                  </div>
                </td>

                <td class="col-description">
                  <div class="description-cell">
                    {{ log.description }}
                  </div>
                </td>

                <td class="col-changes">
                  <div class="changes-cell">
                    <span v-if="log.changeCount > 0" class="changes-badge">
                      {{ log.changeCount }}
                    </span>
                    <span v-else class="no-changes">—</span>
                  </div>
                </td>

                <td class="col-actions">
                  <button 
                    v-if="hasDetails(log)"
                    class="expand-btn"
                    :class="{ expanded: expandedDetails.has(log.id) }"
                    @click.stop="toggleDetails(log.id)"
                  >
                    <i class="pi pi-chevron-down"></i>
                  </button>
                </td>
              </tr>

              <!-- Expanded Details Row -->
              <tr v-if="expandedDetails.has(log.id)" class="details-row">
                <td colspan="7">
                  <div class="details-panel">
                    <!-- Changes Section -->
                    <div v-if="getChangesData(log).length > 0" class="details-section">
                      <h4 class="section-title">
                        <i class="pi pi-file-edit"></i>
                        Field Changes
                      </h4>
                      <div class="changes-grid">
                        <div 
                          v-for="change in getChangesData(log)" 
                          :key="change.field"
                          class="change-item"
                        >
                          <div class="change-field">{{ formatFieldName(change.field) }}</div>
                          <div class="change-values">
                            <div class="value-box old-value">
                              <span class="value-label">Before</span>
                              <span class="value-content">
                                {{ formatDisplayValue(change.beforeValue) || '—' }}
                              </span>
                            </div>
                            <div class="arrow-divider">
                              <i class="pi pi-arrow-right"></i>
                            </div>
                            <div class="value-box new-value">
                              <span class="value-label">After</span>
                              <span class="value-content">
                                {{ formatDisplayValue(change.afterValue) || '—' }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Change Reason -->
                    <div v-if="log.changeReason" class="details-section">
                      <h4 class="section-title">
                        <i class="pi pi-comment"></i>
                        Change Reason
                      </h4>
                      <div class="reason-box">
                        {{ log.changeReason }}
                      </div>
                    </div>

                    <!-- Sensitive Info -->
                    <div v-if="log.canViewSensitiveInfo && (log.ipAddress || log.userAgent)" class="details-section">
                      <h4 class="section-title">
                        <i class="pi pi-shield"></i>
                        Technical Details
                      </h4>
                      <div class="tech-details">
                        <span v-if="log.ipAddress" class="tech-item">
                          <i class="pi pi-map-marker"></i>
                          <strong>IP:</strong> {{ log.ipAddress }}
                        </span>
                        <span v-if="log.userAgent" class="tech-item">
                          <i class="pi pi-desktop"></i>
                          <strong>Browser:</strong> {{ formatUserAgent(log.userAgent) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-container">
        <div class="pagination-info">
          <span class="pagination-text">
            Showing {{ ((currentPage) * filters.size) + 1 }} to 
            {{ Math.min((currentPage + 1) * filters.size, totalElements) }} 
            of {{ totalElements }} activities
          </span>
        </div>

        <div class="pagination-controls">
          <!-- First Page -->
          <button 
            @click="goToPage(0)"
            :disabled="currentPage === 0 || loading"
            class="pagination-btn"
            title="First page"
          >
            <i class="pi pi-angle-double-left"></i>
          </button>

          <!-- Previous Page -->
          <button 
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 0 || loading"
            class="pagination-btn"
            title="Previous page"
          >
            <i class="pi pi-angle-left"></i>
          </button>

          <!-- Page Numbers -->
          <div class="page-numbers">
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="page !== '...' ? goToPage(page) : null"
              :class="['page-btn', { 
                active: page === currentPage, 
                ellipsis: page === '...' 
              }]"
              :disabled="page === '...' || loading"
            >
              {{ page === '...' ? '...' : page + 1 }}
            </button>
          </div>

          <!-- Next Page -->
          <button 
            @click="goToPage(currentPage + 1)"
            :disabled="!hasNextPage || loading"
            class="pagination-btn"
            title="Next page"
          >
            <i class="pi pi-angle-right"></i>
          </button>

          <!-- Last Page -->
          <button 
            @click="goToPage(totalPages - 1)"
            :disabled="currentPage === totalPages - 1 || loading"
            class="pagination-btn"
            title="Last page"
          >
            <i class="pi pi-angle-double-right"></i>
          </button>
        </div>

        <div class="pagination-size">
          <label for="page-size">Per page:</label>
          <select 
            id="page-size"
            v-model="filters.size" 
            @change="onPageSizeChange"
            class="page-size-select"
          >
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import projectService from '@/services/project.service'
import type { 
  ProjectAuditLogResponseDto, 
  ProjectAuditLogQueryParams
} from '@/types/project/audit'

interface Props {
  projectId: number
  showFilters?: boolean
  pageSize?: number
}

const props = withDefaults(defineProps<Props>(), {
  showFilters: false,
  pageSize: 50
})

const { t } = useI18n()
const { notifyError } = useNotifications()

// State
const loading = ref(false)
const error = ref<string | null>(null)
const allAuditLogs = ref<ProjectAuditLogResponseDto[]>([]) // Store all logs
const currentPage = ref(0)
const expandedDetails = ref(new Set<number>())
const showFiltersPanel = ref(props.showFilters)

// Filters
const filters = ref({
  action: '',
  entityType: '',
  size: props.pageSize
})

// Computed - filtered and paginated logs
const filteredAuditLogs = computed(() => {
  let filtered = allAuditLogs.value

  // Apply action filter
  if (filters.value.action) {
    filtered = filtered.filter((log: ProjectAuditLogResponseDto) => log.action === filters.value.action)
  }

  // Apply entity type filter
  if (filters.value.entityType) {
    filtered = filtered.filter((log: ProjectAuditLogResponseDto) => log.entityType === filters.value.entityType)
  }

  return filtered
})

const totalElements = computed(() => filteredAuditLogs.value.length)

const totalPages = computed(() => Math.ceil(filteredAuditLogs.value.length / filters.value.size))

const auditLogs = computed(() => {
  const start = currentPage.value * filters.value.size
  const end = start + filters.value.size
  return filteredAuditLogs.value.slice(start, end)
})

const hasNextPage = computed(() => currentPage.value < totalPages.value - 1)

const visiblePages = computed(() => {
  const pages: (number | string)[] = []
  const total = totalPages.value
  const current = currentPage.value
  const maxVisible = 7 // Maximum number of page buttons to show

  if (total <= maxVisible) {
    // Show all pages if total is small
    for (let i = 0; i < total; i++) {
      pages.push(i)
    }
  } else {
    // Always show first page
    pages.push(0)

    if (current <= 3) {
      // Near the beginning
      for (let i = 1; i <= 4; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(total - 1)
    } else if (current >= total - 4) {
      // Near the end
      pages.push('...')
      for (let i = total - 5; i < total - 1; i++) {
        pages.push(i)
      }
      pages.push(total - 1)
    } else {
      // In the middle
      pages.push('...')
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(total - 1)
    }
  }

  return pages
})

// Methods
const toggleFilters = () => {
  showFiltersPanel.value = !showFiltersPanel.value
}

const clearFilters = () => {
  filters.value = {
    action: '',
    entityType: '',
    size: props.pageSize
  }
  currentPage.value = 0
}

const goToPage = (page: number) => {
  if (page >= 0 && page < totalPages.value && page !== currentPage.value) {
    currentPage.value = page
    // Scroll to top of table
    const tableContainer = document.querySelector('.audit-table-wrapper')
    if (tableContainer) {
      tableContainer.scrollTop = 0
    }
  }
}

const onPageSizeChange = () => {
  currentPage.value = 0
}

const loadAuditLogs = async () => {
  loading.value = true
  error.value = null

  try {
    // Load all logs without pagination (or with a large page size)
    const params = {
      page: 0,
      size: 10000, // Load all logs at once
      sort: 'timestamp',
      direction: 'DESC' as 'ASC' | 'DESC'
    }

    const response = await projectService.getProjectAuditLogs(props.projectId, params)
    
    if (response) {
      const newLogs = response.content.map(log => ({
        ...log,
        formattedTimestamp: formatTimestamp(log.timestamp),
        actionDescription: getActionDescription(log.action, log.entityType),
        actionIcon: getActionIcon(log.action),
        actionColor: getActionColor(log.action),
        canViewSensitiveInfo: canViewSensitiveInfo()
      }))

      allAuditLogs.value = newLogs
    }
  } catch (err: any) {
    error.value = err.message || t('projects.audit.loadError')
    notifyError(error.value)
  } finally {
    loading.value = false
  }
}

const onFilterChange = () => {
  // Reset to first page when filters change
  currentPage.value = 0
}

const toggleDetails = (logId: number) => {
  if (expandedDetails.value.has(logId)) {
    expandedDetails.value.delete(logId)
  } else {
    expandedDetails.value.add(logId)
  }
}

const hasDetails = (log: ProjectAuditLogResponseDto): boolean => {
  return !!(log.beforeValues || log.afterValues || log.changedFields || log.changeReason)
}

// Utility functions
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

  if (diffInMinutes < 1) return t('common.time.justNow')
  if (diffInMinutes < 60) return t('common.time.minutesAgo', { count: diffInMinutes })
  
  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return t('common.time.hoursAgo', { count: diffInHours })
  
  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return t('common.time.daysAgo', { count: diffInDays })
  
  return date.toLocaleDateString()
}

const formatTime = (timestamp: string): string => {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const formatFullTimestamp = (timestamp: string): string => {
  return new Date(timestamp).toLocaleString()
}

const formatUserAgent = (userAgent: string): string => {
  // Simple user agent formatting - extract browser name
  if (userAgent.includes('Chrome')) return 'Chrome'
  if (userAgent.includes('Firefox')) return 'Firefox'
  if (userAgent.includes('Safari')) return 'Safari'
  if (userAgent.includes('Edge')) return 'Edge'
  return 'Browser'
}

const getUserInitials = (userName: string): string => {
  return userName
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2)
}

const formatActionLabel = (action: string): string => {
  return action
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase())
}

const getActionDescription = (action: string, entityType: string): string => {
  const key = `projects.audit.descriptions.${action.toLowerCase()}`
  return t(key, { entity: getEntityLabel(entityType) })
}

const getActionIcon = (action: string): string => {
  const iconMap: Record<string, string> = {
    CREATED: '✨',
    UPDATED: '✏️',
    DELETED: '🗑️',
    CLOSED: '🔒',
    REOPENED: '🔓',
    DOCUMENT_UPLOADED: '📄',
    PHOTO_UPLOADED: '📷',
    DESIGN_UPLOADED: '🎨',
    PHASE_CREATED: '📋',
    TASK_CREATED: '✅',
    ISSUE_CREATED: '⚠️',
    ACCESS_GRANTED: '👥',
    ACCESS_REVOKED: '🚫',
    NOTE_CREATED: '📝',
    PAYMENT_RECORDED: '💰',
    PAYMENT_VALIDATED: '💵'
  }
  return iconMap[action] || '📋'
}

const getActionColor = (action: string): string => {
  const colorMap: Record<string, string> = {
    CREATED: '#10b981',
    UPDATED: '#3b82f6',
    DELETED: '#ef4444',
    CLOSED: '#6b7280',
    REOPENED: '#8b5cf6',
    DOCUMENT_UPLOADED: '#06b6d4',
    PHOTO_UPLOADED: '#f59e0b',
    DESIGN_UPLOADED: '#ec4899',
    PHASE_CREATED: '#14b8a6',
    TASK_CREATED: '#84cc16',
    ISSUE_CREATED: '#f97316',
    ACCESS_GRANTED: '#22c55e',
    ACCESS_REVOKED: '#dc2626',
    NOTE_CREATED: '#6366f1',
    PAYMENT_RECORDED: '#059669',
    PAYMENT_VALIDATED: '#16a34a'
  }
  return colorMap[action] || '#6b7280'
}

const getActionClass = (action: string): string => {
  if (action.includes('CREATED') || action.includes('UPLOADED') || action.includes('GRANTED')) return 'success'
  if (action.includes('DELETED') || action.includes('REVOKED')) return 'danger'
  if (action.includes('UPDATED')) return 'info'
  if (action.includes('CLOSED')) return 'warning'
  return 'default'
}

const getEntityLabel = (entityType: string): string => {
  const labelMap: Record<string, string> = {
    PROJECT: t('projects.audit.entities.project'),
    PROJECT_PHASE: t('projects.audit.entities.phase'),
    PROJECT_TASK: t('projects.audit.entities.task'),
    PROJECT_ISSUE: t('projects.audit.entities.issue'),
    PROJECT_DOCUMENT: t('projects.audit.entities.document'),
    PROJECT_PHOTO: t('projects.audit.entities.photo'),
    PROJECT_DESIGN: t('projects.audit.entities.design'),
    PROJECT_ACCESS: t('projects.audit.entities.access'),
    PROJECT_INTERVENANT: t('projects.audit.entities.intervenant'),
    PROJECT_NOTE: t('projects.audit.entities.note'),
    PROJECT_FINANCE: t('projects.audit.entities.finance'),
    PROJECT_TRANSACTION: t('projects.audit.entities.transaction'),
    PROJECT_NOTIFICATION: t('projects.audit.entities.notification'),
    PROJECT_DOCUMENT_ANNOTATION: t('projects.audit.entities.documentAnnotation'),
    PROJECT_ISSUE_PHOTO: t('projects.audit.entities.issuePhoto'),
    PROVISIONING_REQUEST: t('projects.audit.entities.provisioningRequest')
  }
  return labelMap[entityType] || entityType
}

const canViewSensitiveInfo = (): boolean => {
  // This would check user permissions - simplified for now
  return false
}

// New utility functions for table display
interface ChangeData {
  field: string
  beforeValue: any
  afterValue: any
}

const getChangesData = (log: ProjectAuditLogResponseDto): ChangeData[] => {
  const changes: ChangeData[] = []
  
  // If we have changed fields data, use it
  if (log.changedFields) {
    try {
      const changedFields = JSON.parse(log.changedFields)
      
      // Check if it's an object (expected format from backend)
      if (changedFields && typeof changedFields === 'object' && !Array.isArray(changedFields)) {
        Object.entries(changedFields).forEach(([fieldName, fieldData]: [string, any]) => {
          changes.push({
            field: fieldName,
            beforeValue: fieldData.oldValue,
            afterValue: fieldData.newValue
          })
        })
      }
      // Fallback: handle array format if backend returns it
      else if (Array.isArray(changedFields)) {
        changedFields.forEach((change: any) => {
          changes.push({
            field: change.fieldName || change.field,
            beforeValue: change.beforeValue || change.oldValue,
            afterValue: change.afterValue || change.newValue
          })
        })
      }
    } catch (e) {
      console.warn('Error parsing changedFields:', e, log.changedFields)
    }
  }
  
  // If no changed fields but we have before/after values, compare them
  if (changes.length === 0 && log.beforeValues && log.afterValues) {
    try {
      const before = JSON.parse(log.beforeValues)
      const after = JSON.parse(log.afterValues)
      
      // Compare all fields in the after object
      Object.keys(after).forEach(key => {
        if (before[key] !== after[key]) {
          changes.push({
            field: key,
            beforeValue: before[key],
            afterValue: after[key]
          })
        }
      })
      
      // Also check for removed fields (in before but not in after)
      Object.keys(before).forEach(key => {
        if (!(key in after) && before[key] !== null) {
          changes.push({
            field: key,
            beforeValue: before[key],
            afterValue: null
          })
        }
      })
    } catch (e) {
      console.warn('Error parsing before/after values:', e)
    }
  }
  
  return changes
}

const formatFieldName = (fieldName: string): string => {
  // Convert camelCase or snake_case to readable format
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Add space before uppercase letters
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/^\w/, c => c.toUpperCase()) // Capitalize first letter
    .trim()
}

const formatDisplayValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '—'
  }
  
  if (typeof value === 'boolean') {
    return value ? t('common.yes') : t('common.no')
  }
  
  // Check if it's an encrypted PII field (format: v0000001:base64...)
  if (typeof value === 'string' && value.match(/^v\d+:[A-Za-z0-9+/=]+$/)) {
    return '[Encrypted Data]'
  }
  
  // Check if it's a date string (ISO format)
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/)) {
    try {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        // If it includes time, show full datetime
        if (value.includes('T')) {
          return date.toLocaleString()
        }
        // Otherwise just the date
        return date.toLocaleDateString()
      }
    } catch (e) {
      // Not a valid date, continue
    }
  }
  
  if (typeof value === 'object') {
    // For dates
    if (value instanceof Date) {
      return value.toLocaleDateString()
    }
    // For arrays
    if (Array.isArray(value)) {
      return value.join(', ')
    }
    // For objects, show a summary
    return JSON.stringify(value)
  }
  
  // Format numbers with appropriate precision
  if (typeof value === 'number') {
    // If it's a decimal with more than 2 decimal places, format it
    if (value % 1 !== 0) {
      return value.toFixed(2)
    }
    return String(value)
  }
  
  return String(value)
}

// Watchers
watch(() => props.projectId, () => {
  if (props.projectId) {
    loadAuditLogs()
  }
})

// Lifecycle
onMounted(() => {
  if (props.projectId) {
    loadAuditLogs()
  }
})
</script>

<style scoped>
/* Modern Audit Trail Styles */
.audit-trail-modern {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

/* Header Section */
.audit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.audit-header-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.audit-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.audit-title i {
  font-size: 1.5rem;
}

.audit-stats {
  display: flex;
  gap: 0.75rem;
}

.stat-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.375rem 0.875rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.stat-badge.filtered {
  background: rgba(251, 191, 36, 0.3);
}

.audit-header-right {
  display: flex;
  gap: 0.75rem;
}

.filter-toggle-btn,
.refresh-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.filter-toggle-btn:hover,
.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.filter-toggle-btn.active {
  background: rgba(255, 255, 255, 0.3);
}

.filter-toggle-btn i.pi-chevron-down {
  transition: transform 0.3s ease;
}

.filter-toggle-btn i.pi-chevron-down.rotated {
  transform: rotate(180deg);
}

.refresh-btn {
  padding: 0.5rem 0.75rem;
}

.refresh-btn i.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Filters Panel */
.audit-filters {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 1.5rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  align-items: end;
}

.filter-item label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.5rem;
}

.modern-select {
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: white;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modern-select:hover {
  border-color: #94a3b8;
}

.modern-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.filter-actions {
  display: flex;
  align-items: flex-end;
}

.clear-filters-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.625rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.clear-filters-btn:hover {
  background: #dc2626;
  transform: translateY(-1px);
}

/* Slide Down Animation */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  max-height: 300px;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

/* State Containers */
.state-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 3rem;
}

.loading-state,
.error-state,
.empty-state {
  text-align: center;
  color: #64748b;
}

.loading-state .spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

.loading-state p,
.empty-state p {
  font-size: 1rem;
  color: #64748b;
}

.error-state i,
.empty-state i {
  font-size: 3rem;
  color: #cbd5e1;
  margin-bottom: 1rem;
}

.error-state h3,
.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #334155;
  margin: 0.5rem 0;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #667eea;
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 1rem;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  background: #5568d3;
  transform: translateY(-1px);
}

/* Audit Table */
.audit-table-container {
  overflow: hidden;
}

.audit-table-wrapper {
  overflow-x: auto;
  overflow-y: visible;
}

.audit-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.875rem;
}

.audit-table thead {
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 10;
}

.audit-table thead th {
  padding: 1rem 1.25rem;
  text-align: left;
  font-weight: 600;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}

.audit-table thead th i {
  margin-right: 0.5rem;
  color: #94a3b8;
}

/* Column Widths */
.col-timestamp { width: 140px; }
.col-action { width: 150px; }
.col-entity { width: 150px; }
.col-user { width: 180px; }
.col-description { width: auto; min-width: 250px; }
.col-changes { width: 90px; text-align: center; }
.col-actions { width: 60px; text-align: center; }

/* Table Rows */
.audit-row {
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
}

.audit-row:hover {
  background: #f8fafc;
}

.audit-row.row-expanded {
  background: #f1f5f9;
  box-shadow: inset 0 0 0 1px #e2e8f0;
}

.audit-row td {
  padding: 1rem 1.25rem;
  vertical-align: middle;
}

/* Row Type Styling */
.audit-row.row-success {
  border-left: 3px solid #10b981;
}

.audit-row.row-danger {
  border-left: 3px solid #ef4444;
}

.audit-row.row-info {
  border-left: 3px solid #3b82f6;
}

.audit-row.row-warning {
  border-left: 3px solid #f59e0b;
}

/* Cell Content */
.timestamp-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.time-relative {
  font-weight: 500;
  color: #1e293b;
  font-size: 0.875rem;
}

.time-exact {
  font-size: 0.75rem;
  color: #94a3b8;
}

.action-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.action-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 1rem;
  flex-shrink: 0;
}

.action-label {
  font-weight: 500;
  color: #334155;
}

.entity-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.entity-type {
  font-weight: 500;
  color: #1e293b;
}

.entity-id {
  font-size: 0.75rem;
  color: #94a3b8;
  font-family: 'Courier New', monospace;
}

.user-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-avatar {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.user-name {
  font-weight: 500;
  color: #334155;
}

.description-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #475569;
  line-height: 1.5;
}

.changes-cell {
  text-align: center;
}

.changes-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 14px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0 0.5rem;
}

.no-changes {
  color: #cbd5e1;
  font-size: 1.25rem;
}

.expand-btn {
  width: 32px;
  height: 32px;
  background: #f1f5f9;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s ease;
}

.expand-btn:hover {
  background: #e2e8f0;
  color: #334155;
}

.expand-btn.expanded i {
  transform: rotate(180deg);
}

/* Details Row */
.details-row {
  background: #f8fafc;
}

.details-row td {
  padding: 0;
  border-bottom: 2px solid #e2e8f0;
}

.details-panel {
  padding: 1.5rem 2rem;
  background: white;
  margin: 0.5rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.details-section {
  margin-bottom: 1.5rem;
}

.details-section:last-child {
  margin-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.section-title i {
  color: #667eea;
}

/* Changes Grid */
.changes-grid {
  display: grid;
  gap: 1rem;
}

.change-item {
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e2e8f0;
}

.change-field {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
}

.change-values {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1rem;
  align-items: center;
}

.value-box {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.75rem;
  border-radius: 6px;
  min-height: 60px;
}

.value-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.value-content {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  word-break: break-word;
}

.old-value {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.old-value .value-label {
  color: #dc2626;
}

.old-value .value-content {
  color: #991b1b;
}

.new-value {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.new-value .value-label {
  color: #16a34a;
}

.new-value .value-content {
  color: #166534;
}

.arrow-divider {
  color: #94a3b8;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Reason Box */
.reason-box {
  background: #fefce8;
  border: 1px solid #fde047;
  border-left: 4px solid #eab308;
  padding: 1rem;
  border-radius: 6px;
  color: #713f12;
  line-height: 1.6;
}

/* Technical Details */
.tech-details {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}

.tech-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.875rem;
  font-family: 'Courier New', monospace;
}

.tech-item i {
  color: #94a3b8;
}

.tech-item strong {
  color: #475569;
  font-weight: 600;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: #f8fafc;
  border-top: 2px solid #e2e8f0;
  gap: 1rem;
  flex-wrap: wrap;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-text {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1.5px solid #cbd5e1;
  border-radius: 6px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.pagination-btn:hover:not(:disabled) {
  background: #667eea;
  border-color: #667eea;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f1f5f9;
}

.page-numbers {
  display: flex;
  gap: 0.375rem;
  margin: 0 0.25rem;
}

.page-btn {
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1.5px solid #cbd5e1;
  border-radius: 6px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0 0.5rem;
}

.page-btn:hover:not(:disabled):not(.ellipsis) {
  background: #f1f5f9;
  border-color: #94a3b8;
  transform: translateY(-1px);
}

.page-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
}

.page-btn.ellipsis {
  border: none;
  cursor: default;
  background: transparent;
  color: #94a3b8;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-size {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-size label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.page-size-select {
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1.5px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 70px;
}

.page-size-select:hover {
  border-color: #94a3b8;
}

.page-size-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .changes-grid {
    grid-template-columns: 1fr;
  }
  
  .change-values {
    grid-template-columns: 1fr;
  }
  
  .arrow-divider {
    transform: rotate(90deg);
  }
}

@media (max-width: 768px) {
  .audit-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .audit-header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .audit-header-right {
    width: 100%;
    justify-content: flex-end;
  }
  
  .filters-grid {
    grid-template-columns: 1fr;
  }
  
  .audit-table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .audit-table {
    min-width: 500px;
  }

  .col-entity,
  .col-user {
    display: none;
  }
  
  .details-panel {
    padding: 1rem;
    margin: 0.25rem;
  }
  
  .tech-details {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .pagination-container {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  
  .pagination-info {
    justify-content: center;
  }
  
  .pagination-controls {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .pagination-size {
    justify-content: center;
  }
  
  .page-numbers {
    flex-wrap: wrap;
  }
}

@media (max-width: 480px) {
  .stat-badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.625rem;
  }

  .audit-table {
    min-width: 380px;
  }

  .col-entity,
  .col-user,
  .col-changes {
    display: none;
  }

  .col-timestamp { width: 100px; }
  .col-action { width: 120px; }
  .col-description { min-width: 130px; }
  .col-actions { width: 44px; }
  
  .filter-toggle-btn span:not(.pi) {
    display: none;
  }

  .filter-toggle-btn,
  .refresh-btn {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
  }
  
  .pagination-btn,
  .page-btn {
    min-width: 32px;
    height: 32px;
    font-size: 0.75rem;
  }
  
  .page-numbers {
    max-width: 100%;
    overflow-x: auto;
    padding: 0.25rem 0;
  }
  
  .pagination-text {
    font-size: 0.75rem;
    text-align: center;
  }
  
  .page-size-select {
    font-size: 0.75rem;
    padding: 0.375rem 0.5rem;
    min-width: 60px;
  }
}

@media (max-width: 360px) {
  .audit-table {
    min-width: 320px;
  }

  .col-timestamp { width: 80px; }
  .col-action { width: 96px; }
  .col-description { min-width: 104px; }
  .col-actions { width: 40px; }
}
</style>